import { DateTime } from 'luxon'
import { datePattern, getCalendarTimeZone, getDisplayTimeZone, timePattern, uses24HourClock } from '../../lib/format.js'

// Typed date / datetime entry shared by DateTimePickerField and its
// consumers. Kept in a plain `.js` sibling (like phoneValidation.js) so the
// pure helpers can be imported without the component — which also keeps the
// `.jsx` exporting only a component (clean Fast Refresh) and lets these be
// unit-tested directly. Minute-granular by design: seconds are neither
// entered nor shown.
//
// Region & formats: the shape follows the person's date format and 12/24-hour
// clock, and the value is read and written in THEIR display zone (their own
// timezone, else the company's) — the same zone every screen shows times in —
// so what they type is exactly what they see afterwards. The stored value is
// still a UTC instant.

/** Default (dd/MM/yyyy, 12-hour) shapes, kept for existing imports. Prefer
 *  the functions below, which follow the person's preferences. */
export const DATETIME_FORMAT = 'dd/MM/yyyy hh:mm a'
export const DATETIME_PLACEHOLDER = 'DD/MM/YYYY HH:MM (AM/PM)'
export const DATE_FORMAT = 'dd/MM/yyyy'
export const DATE_PLACEHOLDER = 'DD/MM/YYYY'

const PLACEHOLDERS = {
  'dd/MM/yyyy': 'DD/MM/YYYY',
  'MM/dd/yyyy': 'MM/DD/YYYY',
  'yyyy-MM-dd': 'YYYY-MM-DD',
  'd MMM yyyy': 'D MMM YYYY',
}

/** The person's date entry shape (Luxon tokens). */
export function dateFormat() {
  return datePattern()
}

/** The person's date + time entry shape (Luxon tokens). */
export function dateTimeFormat() {
  return `${datePattern()} ${timePattern()}`
}

/** Placeholder text for a date field, e.g. "DD/MM/YYYY" or "MM/DD/YYYY". */
export function datePlaceholder() {
  return PLACEHOLDERS[datePattern()] ?? DATE_PLACEHOLDER
}

/** Placeholder text for a date + time field. */
export function dateTimePlaceholder() {
  return `${datePlaceholder()} ${uses24HourClock() ? 'HH:MM' : 'HH:MM (AM/PM)'}`
}

const zone = () => getDisplayTimeZone() || 'local'
const opts = () => ({ zone: zone(), locale: 'en-GB' })
// Date-only values are CALENDAR DAYS — read on the company's calendar (the
// same zone formatDate / dayKey use), so a picked day is the day everyone sees.
const dayZone = () => getCalendarTimeZone() || 'local'
const dayOpts = () => ({ zone: dayZone(), locale: 'en-GB' })

/** Lenient twin of a pattern: single-digit day / month / hour accepted. */
const lenient = (fmt) =>
  fmt.replace('dd', 'd').replace('MM', 'M').replace('hh', 'h').replace('HH', 'H')

function parseWith(s, fmt, o = opts()) {
  for (const f of [fmt, lenient(fmt)]) {
    const dt = DateTime.fromFormat(s, f, o)
    if (dt.isValid) return dt
    // The written-out format ("26 Sept 2026") — en-GB spells September
    // "Sept", but people often type "Sep"; accept the en-US short name too.
    if (f.includes('MMM')) {
      const us = DateTime.fromFormat(s, f, { ...o, locale: 'en-US' })
      if (us.isValid) return us.setLocale(o.locale)
    }
  }
  return null
}

const clean = (value) =>
  (value ?? '')
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    // Luxon reads the meridiem case-insensitively, but normalise "pm"/"Pm".
    .replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())

/** Parse a typed date + time (in the person's format, e.g.
 *  "04/07/2026 09:05 PM" or "2026-07-04 21:05") into a Luxon DateTime in the
 *  person's display zone. Tolerates parens, extra spaces and single-digit
 *  parts. Null for empty / unparseable input. */
export function parseDateTimeInput(value) {
  const s = clean(value)
  return s ? parseWith(s, dateTimeFormat()) : null
}

/** Stored ISO instant (or a Luxon DateTime) → the display string in the
 *  person's zone and format. "" for nullish / unparseable input. */
export function formatDateTimeInput(input) {
  if (!input) return ''
  const dt = typeof input === 'string' ? DateTime.fromISO(input, opts()) : input.setZone(zone())
  return dt && dt.isValid
    ? dt.toFormat(dateTimeFormat()).replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())
    : ''
}

// ── Date-only variants ──────────────────────────────────────────────────────
// Some fields (e.g. an engagement END date) are day-granular — a time would be
// noise — so `DateTimePickerField dateOnly` renders a date-only picker.

/** Parse a typed date (person's format) → a Luxon DateTime at START of that
 *  day in the person's zone. Null for empty / unparseable input. */
export function parseDateInput(value) {
  const s = clean(value)
  const dt = s ? parseWith(s, dateFormat(), dayOpts()) : null
  return dt ? dt.startOf('day') : null
}

/** Stored ISO instant (or a Luxon DateTime) → the date string in the
 *  person's zone and format. "" for nullish / unparseable input. */
export function formatDateInput(input) {
  if (!input) return ''
  const dt = typeof input === 'string' ? DateTime.fromISO(input, dayOpts()) : input.setZone(dayZone())
  return dt && dt.isValid ? dt.toFormat(dateFormat()) : ''
}
